/**
 * STFFLG-4185: the Settings pages as plain data, in menu order.
 *
 * This file imports NOTHING, on purpose: the Cypress catalogue check (20-permission-catalog)
 * imports it directly to compare every page with the backend's Settings rows, and the Cypress
 * bundle cannot resolve the route table, the store or `~/src/...` paths. settingsPages.ts adds the
 * icons and the runtime conditions on top; settingsPermission.ts decides each row from it.
 */

/** The role categories whose matrix catalogue carries a Settings row (Candidate has none). */
export type SettingsRoleCategory = 'Platform' | 'MSP' | 'Agency' | 'Company'

/** The Settings rows a page can name: each is a child key under the matrix's Settings module. */
export type SettingsPermissionKey =
  | 'event_categories'
  | 'compliance_fields'
  | 'esign_documents'
  | 'zoom_config'
  | 'work_authorization'
  | 'help_faq'
  | 'faq_admin'
  | 'invoice_templates'

/** An action the role editor can offer on a row (mspplatform `availableOperations`). */
export type SettingsOperation = 'View' | 'Create' | 'Edit' | 'Delete'

/**
 * What a role whose tree has NO node for the row may do. A present node, an explicit all-false
 * deny included, always decides by its own flags; this is only for a row the tree lacks, which
 * still happens after the one-shot (settings_pages_category_v1) has run:
 *   - a signed-in user keeps the tree redux-persist saved before the deploy until they sign in again;
 *   - a legacy accessList-only role has no tree at all, and the one-shot skips it;
 *   - the one-shot skips a role whose stored category differs from its predefined config's.
 * So each default is what the one-shot would have written, which is also what the page allowed
 * before it had a row:
 *   'allow'                — View allowed: Work Authorization and Help / FAQ were open to everyone.
 *   'rolesPermissionsView' — every action the row offers is allowed iff the role holds Global >
 *                            Roles & Permissions View, the stand-in these pages used to sit behind.
 *   'deny'                 — denied: these rows existed before, and an absent one already read as no
 *                            access.
 */
export type SettingsAbsentDefault = 'allow' | 'rolesPermissionsView' | 'deny'

export interface SettingsPageData {
  /** Nav key: the nav item's test id is `settings-nav-item-${id}`. */
  id: string
  /** The nav label, and the label of the page's row in the role matrix (eSign excepted). */
  label: string
  /** Route path, no leading slash (as in routeConfig). */
  path: string
  /** The routeConfig id of the page's route. */
  routeId: string
  /** The page's child key under the role matrix's Settings module (= its route's submoduleKey). */
  permissionKey: SettingsPermissionKey
  /** The categories whose matrix catalogue lists this row (mirrors the backend module-config). */
  categories: readonly SettingsRoleCategory[]
  /** The actions the row offers, as the backend catalogue lists them. */
  availableOperations: readonly SettingsOperation[]
  absentDefault: SettingsAbsentDefault
  /** Group header shown above the first of a run of pages sharing it. */
  group?: string
}

const ALL_FOUR: readonly SettingsOperation[] = ['View', 'Create', 'Edit', 'Delete']

/** In menu order: the first visible entry is where /settings lands. */
export const SETTINGS_PAGES_DATA: readonly SettingsPageData[] = [
  {
    id: 'event-categories',
    label: 'Event Categories',
    path: 'settings/event-categories',
    routeId: 'settings-event-categories',
    permissionKey: 'event_categories',
    categories: ['Platform', 'MSP', 'Agency'],
    availableOperations: ALL_FOUR,
    absentDefault: 'deny',
  },
  {
    id: 'compliance-fields',
    label: 'Compliance Fields',
    path: 'settings/compliance-fields',
    routeId: 'settings-compliance-fields',
    permissionKey: 'compliance_fields',
    categories: ['Platform', 'MSP', 'Agency'],
    availableOperations: ALL_FOUR,
    absentDefault: 'deny',
  },
  {
    // STFFLG-3958: both eSign pages are governed by the one Documents permission
    // (settings > esign > esign_documents): managing files and signature requests is one job.
    // The matrix row keeps its own name, "Documents", under eSign.
    id: 'documents-files',
    label: 'Files',
    path: 'settings/documents/files',
    routeId: 'settings-documents-files',
    permissionKey: 'esign_documents',
    categories: ['Agency'],
    availableOperations: ALL_FOUR,
    absentDefault: 'deny',
    group: 'Documents',
  },
  {
    id: 'documents-signatures',
    label: 'Signatures',
    path: 'settings/documents/signatures',
    routeId: 'settings-documents-signatures',
    permissionKey: 'esign_documents',
    categories: ['Agency'],
    availableOperations: ALL_FOUR,
    absentDefault: 'deny',
    group: 'Documents',
  },
  {
    id: 'zoom-setup',
    label: 'Zoom Setup',
    path: 'settings/zoom-setup',
    routeId: 'settings-zoom-setup',
    permissionKey: 'zoom_config',
    categories: ['Platform', 'MSP', 'Agency'],
    availableOperations: ALL_FOUR,
    // The nav showed it on the row OR Roles & Permissions, and the App Configuration card was
    // editable on either.
    absentDefault: 'rolesPermissionsView',
  },
  {
    // STFFLG-3849: the key, path and route stay 'e-verify'; the page keeps its full
    // "Work Authorization Expiration Tasks" title.
    id: 'e-verify',
    label: 'Work Authorization',
    path: 'settings/e-verify',
    routeId: 'settings-e-verify',
    permissionKey: 'work_authorization',
    categories: ['Platform', 'MSP', 'Agency'],
    availableOperations: ['View'],
    absentDefault: 'allow',
  },
  {
    id: 'help',
    label: 'Help / FAQ',
    path: 'settings/help',
    routeId: 'settings-help',
    permissionKey: 'help_faq',
    categories: ['Platform', 'MSP', 'Agency', 'Company'],
    availableOperations: ['View'],
    absentDefault: 'allow',
  },
  {
    id: 'faq-admin',
    label: 'FAQ Admin',
    path: 'settings/faq-admin',
    routeId: 'settings-faq-admin',
    permissionKey: 'faq_admin',
    categories: ['Platform'],
    availableOperations: ALL_FOUR,
    absentDefault: 'rolesPermissionsView',
  },
  {
    id: 'invoice-templates',
    label: 'Invoice Templates',
    path: 'settings/invoice-templates',
    routeId: 'settings-invoice-templates',
    permissionKey: 'invoice_templates',
    categories: ['Platform'],
    availableOperations: ['View', 'Edit'],
    absentDefault: 'rolesPermissionsView',
  },
]
